import { Component, inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ReportsService } from '../../../services/reports/reports.service';
import { DisplayService } from '../../../services/reports/display.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { IconButtonComponent } from '../../core/buttons/icon-button.component';
import { InputComponent } from '../../core/controls/input.component';
import { SelectComponent } from '../../core/controls/select.component';
import { InputFieldComponent } from '../../core/fields/input-field.component';
import { TextareaFieldComponent } from '../../core/fields/textarea-field.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';

@Component({
  selector: 'app-report-email',
  imports: [
    PageHeaderComponent,
    SelectComponent,
    InputComponent,
    ActionRowComponent,
    ButtonComponent,
    IconButtonComponent,
    InputFieldComponent,
    TextareaFieldComponent,
  ],
  template: `
    @if (display.showEmailRecipient()) {
      <div class="tw-pb-4">
        <app-page-header label="Additional email recipients" headerSize="h3" />
        <app-action-row>
          <app-button
            left1
            emphasis="Low"
            label="Add recipient"
            (click)="addEmail()"
          />
        </app-action-row>
        <div class="tw-pb-4"></div>

        @for (
          emailFg of fg.controls.emails.controls;
          track emailFg;
          let i = $index;
          let last = $last
        ) {
          <div class="tw-flex tw-flex-col md:tw-flex-row app-fade-in" [class.tw-pb-2]="!last">
            <div class="tw-w-full md:tw-basis-2/5">
              <app-input
                [control]="$any(emailFg).controls.email"
                label="Email address"
                class="tw-grow"
              />
            </div>
            <div class="tw-w-full md:tw-basis-2/5">
              <app-select
                [control]="$any(emailFg).controls.option"
                label="Send"
                optionLabel="name"
                optionValue="code"
                [options]="notifyOptions"
                [filter]="false"
                [showClear]="false"
                [virtualScroll]="false"
              ></app-select>
            </div>

            <app-icon-button
              icon="fa-light fa-xmark"
              (click)="removeEmail(i)"
              ariaLabel="Remove email recipient"
            />
          </div>
        }
      </div>
    }
    @if (display.showCustomEmail()) {
      <div>
        <app-page-header label="Customize email" headerSize="h3" />
        <div class="tw-italic tw-text-sm tw-pb-4">
          The below information will be added to the Sedgwick standard email
          message. (optional) Sensitive Personal Identifiable Information (SPII)
          should not be included in the subject line, body or closing sections.
        </div>
        <div class="tw-flex tw-flex-col tw-gap-1 tw-w-full">
          <app-input-field
            label="Subject"
            labelClasses="tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
            valueClasses="tw-basis-full sm:tw-basis-[70%]"
            [control]="fg.controls.emailSubject"
          />
          <app-textarea-field
            label="Body"
            labelClasses="tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
            valueClasses="tw-basis-full sm:tw-basis-[70%]"
            [control]="fg.controls.emailBody"
          />
          <app-textarea-field
            label="Closing"
            labelClasses="tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
            valueClasses="tw-basis-full sm:tw-basis-[70%]"
            [control]="fg.controls.emailClosing"
          />
        </div>
      </div>
    }
  `,
  styles: ``,
})
export class ReportEmailComponent extends BaseComponent implements OnInit {
  protected reports = inject(ReportsService);
  protected display = inject(DisplayService);

  fg = new FormGroup({
    emailSubject: new FormControl<string>(null),
    emailBody: new FormControl<string>(null, [Validators.maxLength(4000)]),
    emailClosing: new FormControl<string>(null, [Validators.maxLength(4000)]),
    emails: new FormArray([]),
  });

  notifyOptions = [
    { name: 'Send for all reports', code: '1' },
    { name: 'Send for non-empty reports', code: '2' },
  ];

  ngOnInit(): void {
    this.loadSavedValues();
  }

  private loadSavedValues(): void {
    const report = this.reports.currentReport();
    const reportRequest = report.reportRequest;

    if (reportRequest.notifications && reportRequest.notifications.length > 0) {
      const additionalEmails = reportRequest.notifications.filter(
        (n) => !n.ownerEmail,
      );

      this.fg.controls.emails.clear();

      additionalEmails.forEach((notification) => {
        const optionValue = notification.suppressEmail ? '2' : '1'; // '2' = non-empty, '1' = all reports

        const emailGroup = new FormGroup({
          email: new FormControl(notification.email, [
            Validators.required,
            Validators.email,
          ]),
          option: new FormControl(optionValue, [Validators.required]),
        });
        this.fg.controls.emails.push(emailGroup);
      });
    }

    if (reportRequest.customEmail) {
      this.fg.controls.emailSubject.setValue(
        reportRequest.customEmail.emailSubject || '',
      );
      this.fg.controls.emailBody.setValue(
        reportRequest.customEmail.emailBody || '',
      );
      this.fg.controls.emailClosing.setValue(
        reportRequest.customEmail.emailClose || '',
      );
    }
  }

  updateReport(): void {
    this.reports.currentReport.update((report) => {
      if (!report.reportRequest) return report;

      const notifications: {
        email: string;
        ownerEmail: boolean;
        suppressEmail: boolean;
      }[] = [];

      this.fg.controls.emails.controls.forEach((emailGroup: any) => {
        const email = emailGroup.controls.email.value;
        const option = emailGroup.controls.option.value;

        if (email) {
          notifications.push({
            email: email,
            ownerEmail: false,
            suppressEmail: option === '2',
          });
        }
      });

      report.reportRequest.notifications = notifications;

      if (!report.reportRequest.customEmail) {
        report.reportRequest.customEmail = {
          emailSubject: '',
          emailBody: '',
          emailClose: '',
        };
      }

      report.reportRequest.customEmail.emailSubject =
        this.fg.controls.emailSubject.value || '';
      report.reportRequest.customEmail.emailBody =
        this.fg.controls.emailBody.value || '';
      report.reportRequest.customEmail.emailClose =
        this.fg.controls.emailClosing.value || '';

      return report;
    });
  }

  addEmail(): void {
    this.fg.controls.emails.push(
      new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
        option: new FormControl('1', [Validators.required]),
      }),
    );
  }

  removeEmail(index: number): void {
    this.fg.controls.emails.removeAt(index);
  }
}
